import React, { useState, useEffect, useRef } from 'react';
import { investmentService, userService } from '../../services/api';
import { Plus, Search, Edit2, Trash2, X, IndianRupee, TrendingUp, Calendar, CheckCircle, Clock, Wallet, Briefcase, User, ChevronDown, Filter, LayoutGrid, List, Printer, Download } from 'lucide-react';
import InvestmentPrint from './InvestmentPrint';

function InvestmentsManagement() {
  const [investments, setInvestments] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingInvestment, setEditingInvestment] = useState(null);
  const [selectedForPrint, setSelectedForPrint] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const userDropdownRef = useRef(null);
  const [formData, setFormData] = useState({
    user_id: '',
    investment_number: '',
    amount: '',
    type: 'share',
    interest_rate: '',
    start_date: '',
    maturity_date: '',
    status: 'pending',
    notes: ''
  });

  useEffect(() => {
    loadInvestments();
    loadUsers();
    
    const handleClickOutside = (event) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target)) {
        setShowUserDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadInvestments = async () => {
    setLoading(true);
    try {
      const response = await investmentService.index();
      console.log('Investments API response:', response);
      // Handle both direct array and paginated response
      const data = response?.data || response;
      if (Array.isArray(data)) {
        setInvestments(data);
      } else {
        console.error('Expected array of investments but got:', data);
        setInvestments([]);
      }
    } catch (error) {
      console.error('Error loading investments:', error);
      setInvestments([]);
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const data = await userService.index();
      setUsers(Array.isArray(data) ? data : data?.data || []);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingInvestment) {
        await investmentService.update(editingInvestment.id, formData);
      } else {
        await investmentService.store(formData);
      }
      closeModal();
      await loadInvestments();
    } catch (error) {
      alert('Error saving investment: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleEdit = (investment) => {
    setEditingInvestment(investment);
    setFormData({
      user_id: investment.user_id || '',
      investment_number: investment.investment_number || '',
      amount: investment.amount || '',
      type: investment.type || 'share',
      interest_rate: investment.interest_rate || '',
      start_date: investment.start_date || '',
      maturity_date: investment.maturity_date || '',
      status: investment.status || 'pending',
      notes: investment.notes || ''
    });
    
    const user = users.find(u => u.id === investment.user_id);
    if (user) {
      setUserSearch(user.name);
    }
    
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this investment record? This action cannot be undone.')) {
      try {
        await investmentService.destroy(id);
        loadInvestments();
      } catch (error) {
        console.error('Error deleting investment:', error);
      }
    }
  };

  const handleApprove = async (id) => {
    try {
      await investmentService.approve(id);
      loadInvestments();
    } catch (error) {
      alert('Error approving: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleWithdraw = async (id) => {
    if (window.confirm('Process withdrawal for this investment? This will change status to withdrawn.')) {
      try {
        await investmentService.withdraw(id);
        loadInvestments();
      } catch (error) {
        alert('Error withdrawing: ' + (error.response?.data?.message || error.message));
      }
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingInvestment(null);
    setUserSearch('');
    setFormData({ 
      user_id: '', 
      investment_number: '', 
      amount: '', 
      type: 'share', 
      interest_rate: '', 
      start_date: '', 
      maturity_date: '', 
      status: 'pending',
      notes: ''
    });
  };

  const handleExportCSV = () => {
    if (investments.length === 0) {
      alert('No data to export.');
      return;
    }

    const headers = ['Investment Ref #', 'Investor Name', 'Asset Type', 'Principal Amount (₹)', 'Annual Yield (%)', 'Current Valuation (₹)', 'Maturity Amount (₹)', 'Start Date', 'Maturity Target', 'Portfolio Status'];
    
    const rows = investments.map(inv => {
      const user = users.find(u => u.id === inv.user_id);
      const valuation = calculateCurrentValuation(inv);
      
      // Helper to escape values for CSV
      const escape = (val) => {
        if (val === null || val === undefined) return '""';
        const str = String(val).replace(/"/g, '""');
        return `"${str}"`;
      };

      return [
        escape(inv.investment_number),
        escape(user?.name || 'Unknown Investor'),
        escape(inv.type?.toUpperCase().replace('_', ' ')),
        escape(inv.amount),
        escape(inv.interest_rate),
        escape(valuation.toFixed(2)),
        escape(inv.maturity_amount ? parseFloat(inv.maturity_amount).toFixed(2) : 'N/A'),
        escape(inv.start_date),
        escape(inv.maturity_date || 'Open Ended'),
        escape(inv.status?.toUpperCase())
      ];
    });

    const csvString = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `Ortus_Investments_Export_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const filteredInvestments = investments.filter(i => {
    const user = users.find(u => u.id === i.user_id);
    const searchLower = searchQuery.toLowerCase();
    return (
      i.investment_number?.toLowerCase().includes(searchLower) ||
      user?.name?.toLowerCase().includes(searchLower) ||
      i.type?.toLowerCase().includes(searchLower) ||
      i.status?.toLowerCase().includes(searchLower)
    );
  });

  const filteredUsers = users.filter(u => 
    u.name?.toLowerCase().includes(userSearch.toLowerCase()) ||
    u.email?.toLowerCase().includes(userSearch.toLowerCase())
  );

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active': return <TrendingUp size={12} />;
      case 'matured': return <CheckCircle size={12} />;
      case 'withdrawn': return <Wallet size={12} />;
      default: return <Clock size={12} />;
    }
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case 'active': return 'bg-red-500/10 text-red-500 border-red-500/20';
      case 'matured': return 'bg-blue-500/10 text-blue-500 border-blue-500/20';
      case 'withdrawn': return 'bg-slate-500/10 text-slate-400 border-slate-500/20';
      default: return 'bg-amber-500/10 text-amber-500 border-amber-500/20';
    }
  };

  const calculateCurrentValuation = (investment) => {
    const principal = parseFloat(investment.amount || 0);
    const rate = parseFloat(investment.interest_rate || 0) / 100;
    const startDate = new Date(investment.start_date);
    const today = new Date();
    
    if (today <= startDate) return principal;

    const diffTime = Math.abs(today - startDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    const earnedInterest = principal * (rate * (diffDays / 365));
    return principal + earnedInterest;
  };

  if (loading) return (
    <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
      <div className="w-12 h-12 border-4 border-red-500 border-t-transparent rounded-full animate-spin"></div>
      <p className="text-slate-400 font-medium animate-pulse">Calculating portfolio performance...</p>
    </div>
  );

  return (
    <div className="space-y-6 max-w-[1600px] mx-auto">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 bg-slate-900/40 p-6 rounded-[2rem] border border-slate-800/50 backdrop-blur-sm">
        <div className="flex items-center gap-4">
          <div className="h-14 w-14 rounded-2xl bg-red-500/10 flex items-center justify-center text-red-500 border border-red-500/20">
            <TrendingUp size={28} />
          </div>
          <div>
            <h2 className="text-3xl font-black text-white tracking-tight">Investment Portfolio</h2>
            <div className="flex items-center gap-2 mt-1">
              <span className="h-2 w-2 rounded-full bg-red-500 animate-pulse"></span>
              <p className="text-sm text-slate-500 font-medium tracking-wide">Managing {investments.length} Active Capital Assets</p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <button
            onClick={handleExportCSV}
            className="flex items-center justify-center gap-2 bg-slate-800 text-white px-6 py-4 rounded-2xl font-black uppercase tracking-widest hover:bg-slate-700 transition-all shadow-xl active:scale-95"
          >
            <Download size={20} />
            Export CSV
          </button>
          <button
            onClick={() => setShowModal(true)}
            className="flex items-center justify-center gap-2 bg-red-600 text-white px-8 py-4 rounded-2xl font-black uppercase tracking-widest hover:bg-red-500 transition-all shadow-2xl shadow-red-900/40 group active:scale-95"
          >
            <Plus size={20} className="group-hover:rotate-90 transition-transform duration-300" />
            Log New Asset
          </button>
        </div>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {[
          { label: 'Total Capital', value: `₹${investments.reduce((acc, curr) => acc + parseFloat(curr.amount || 0), 0).toLocaleString()}`, icon: IndianRupee, color: 'text-red-500', bg: 'bg-red-500/5' },
          { label: 'Active ROI', value: `${(investments.filter(i => i.status === 'active').length > 0 ? (investments.filter(i => i.status === 'active').reduce((acc, curr) => acc + parseFloat(curr.interest_rate || 0), 0) / investments.filter(i => i.status === 'active').length).toFixed(2) : 0)}% Avg`, icon: TrendingUp, color: 'text-blue-500', bg: 'bg-blue-500/5' },
          { label: 'Pending Approval', value: investments.filter(i => i.status === 'pending').length, icon: Clock, color: 'text-amber-500', bg: 'bg-amber-500/5' },
          { label: 'Asset Classes', value: new Set(investments.map(i => i.type)).size, icon: Briefcase, color: 'text-purple-500', bg: 'bg-purple-500/5' },
        ].map((stat, idx) => (
          <div key={idx} className={`${stat.bg} p-6 rounded-3xl border border-white/5 flex items-center gap-4`}>
            <div className={`h-12 w-12 rounded-xl flex items-center justify-center ${stat.color} bg-white/5`}>
              <stat.icon size={24} />
            </div>
            <div>
              <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">{stat.label}</p>
              <p className="text-2xl font-black text-white">{stat.value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Main Content Table */}
      <div className="bg-slate-900/50 rounded-[2.5rem] border border-slate-800 shadow-2xl overflow-hidden backdrop-blur-xl">
        <div className="p-6 border-b border-slate-800 bg-slate-950/30 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
            <input
              type="text"
              placeholder="Search portfolio by ID, investor or type..."
              className="w-full bg-slate-950/50 border border-slate-800 rounded-2xl py-3.5 pl-12 pr-4 text-sm text-slate-200 focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all placeholder:text-slate-600"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div className="flex items-center gap-3">
            <button className="p-3 bg-slate-900 border border-slate-800 rounded-xl text-slate-400 hover:text-white transition-colors">
              <Filter size={18} />
            </button>
            <div className="flex bg-slate-950 p-1 rounded-xl border border-slate-800">
              <button className="p-2 bg-slate-800 text-white rounded-lg shadow-lg">
                <List size={16} />
              </button>
              <button className="p-2 text-slate-500 hover:text-slate-300">
                <LayoutGrid size={16} />
              </button>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-950/50">
                <th className="px-8 py-5 text-[10px] uppercase font-black tracking-widest text-slate-500 border-b border-slate-800">Investor Entity</th>
                <th className="px-8 py-5 text-[10px] uppercase font-black tracking-widest text-slate-500 border-b border-slate-800">Financial Data</th>
                <th className="px-8 py-5 text-[10px] uppercase font-black tracking-widest text-slate-500 border-b border-slate-800">Strategy Class</th>
                <th className="px-8 py-5 text-[10px] uppercase font-black tracking-widest text-slate-500 border-b border-slate-800">Maturity Timeline</th>
                <th className="px-8 py-5 text-[10px] uppercase font-black tracking-widest text-slate-500 border-b border-slate-800">Portfolio Status</th>
                <th className="px-8 py-5 text-[10px] uppercase font-black tracking-widest text-slate-500 border-b border-slate-800 text-right">Operations</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/50">
              {filteredInvestments.map((inv) => {
                const user = users.find(u => u.id === inv.user_id);
                return (
                  <tr key={inv.id} className="hover:bg-red-500/[0.02] transition-colors group">
                    <td className="px-8 py-6">
                      <div className="flex items-center gap-4">
                        <div className="h-12 w-12 rounded-2xl bg-gradient-to-br from-slate-800 to-slate-950 flex items-center justify-center text-red-500 border border-slate-700 shadow-inner group-hover:scale-110 transition-transform">
                          <User size={20} />
                        </div>
                        <div>
                          <div className="font-black text-slate-100 group-hover:text-red-400 transition-colors uppercase tracking-tight">
                            {user ? user.name : 'Unknown Investor'}
                          </div>
                          <div className="text-[10px] text-slate-500 font-mono mt-0.5 flex items-center gap-1.5 uppercase">
                            <span className="text-red-500/50">Ref:</span> {inv.investment_number}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-8 py-6">
                      <div className="space-y-1.5">
                        <div className="text-lg text-white font-black flex items-center gap-1.5 tracking-tight" title="Principal Amount">
                          <span className="text-red-500 font-medium">₹</span>
                          {parseFloat(inv.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        </div>
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center gap-2">
                            <span className="text-[10px] font-black px-2 py-0.5 rounded-md bg-red-500/10 text-red-500 border border-red-500/20">
                              {inv.interest_rate}% APR
                            </span>
                          </div>
                          <div className="text-[11px] font-bold text-slate-300 flex items-center gap-1" title="Dynamic Current Valuation">
                            <TrendingUp size={12} className="text-red-500" />
                            Value: ₹{calculateCurrentValuation(inv).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-8 py-6">
                      <div className="text-[10px] uppercase font-black tracking-[0.2em] text-slate-400 bg-slate-950/80 px-4 py-2 rounded-xl border border-slate-800 inline-flex items-center gap-2 group-hover:border-red-500/30 transition-colors shadow-sm">
                        <div className="h-1.5 w-1.5 rounded-full bg-red-500"></div>
                        {inv.type?.replace('_', ' ')}
                      </div>
                    </td>
                    <td className="px-8 py-6">
                      <div className="space-y-2">
                        <div className="text-[11px] text-slate-300 font-bold flex items-center gap-2.5">
                          <Calendar size={13} className="text-red-500/60" />
                          {new Date(inv.start_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                        </div>
                        <div className="text-[11px] text-slate-500 font-medium flex items-center gap-2.5">
                          <Clock size={13} className="text-slate-700" />
                          {inv.maturity_date ? new Date(inv.maturity_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : 'Open Ended'}
                        </div>
                      </div>
                    </td>
                    <td className="px-8 py-6">
                      <span className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-[10px] font-black tracking-widest uppercase border ${getStatusStyle(inv.status)} shadow-lg shadow-black/5`}>
                        <div className={`h-1.5 w-1.5 rounded-full bg-current animate-pulse`}></div>
                        {inv.status}
                      </span>
                    </td>
                    <td className="px-8 py-6 text-right">
                      <div className="flex justify-end gap-2.5 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => setSelectedForPrint(inv)}
                          className="p-2.5 text-slate-400 hover:bg-slate-500/10 rounded-xl transition-all border border-transparent hover:border-slate-500/30"
                          title="Print Certificate"
                        >
                          <Printer size={18} />
                        </button>
                        {inv.status === 'pending' && (
                          <button
                            onClick={() => handleApprove(inv.id)}
                            className="p-2.5 text-red-500 hover:bg-red-500/10 rounded-xl transition-all border border-transparent hover:border-red-500/30"
                            title="Verify Asset"
                          >
                            <CheckCircle size={18} />
                          </button>
                        )}
                        {inv.status === 'active' && (
                          <button
                            onClick={() => handleWithdraw(inv.id)}
                            className="p-2.5 text-amber-500 hover:bg-amber-500/10 rounded-xl transition-all border border-transparent hover:border-amber-500/30"
                            title="Execute Exit"
                          >
                            <Wallet size={18} />
                          </button>
                        )}
                        <button
                          onClick={() => handleEdit(inv)}
                          className="p-2.5 text-blue-400 hover:bg-blue-400/10 rounded-xl transition-all border border-transparent hover:border-blue-400/30"
                          title="Modify Entry"
                        >
                          <Edit2 size={18} />
                        </button>
                        <button
                          onClick={() => handleDelete(inv.id)}
                          className="p-2.5 text-red-400 hover:bg-red-400/10 rounded-xl transition-all border border-transparent hover:border-red-400/30"
                          title="Purge Record"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {filteredInvestments.length === 0 && (
            <div className="text-center py-32 bg-slate-950/20">
              <div className="bg-slate-800/50 w-24 h-24 rounded-[2.5rem] flex items-center justify-center mx-auto mb-6 text-slate-700 border border-white/5 shadow-2xl">
                <TrendingUp size={48} />
              </div>
              <h3 className="text-xl font-black text-slate-200">Empty Portfolio</h3>
              <p className="text-slate-500 mt-2 max-w-xs mx-auto text-sm leading-relaxed">No investment matches found. Initiate a new capital entry to begin tracking performance.</p>
            </div>
          )}
        </div>
      </div>

      {/* Modern Modal / Side Panel */}
      {showModal && (
        <div className="fixed inset-0 bg-slate-950/90 backdrop-blur-md flex items-center justify-center z-[100] p-4 transition-all duration-500">
          <div className="bg-slate-900 border border-slate-800 rounded-[3rem] shadow-2xl w-full max-w-2xl overflow-hidden animate-in fade-in zoom-in slide-in-from-bottom-10 duration-500">
            <div className="px-10 py-8 border-b border-slate-800 flex justify-between items-center bg-slate-950/50">
              <div>
                <h3 className="text-2xl font-black text-white tracking-tight">
                  {editingInvestment ? 'Update Performance' : 'Log New Capital Entry'}
                </h3>
                <p className="text-xs text-slate-500 font-bold uppercase tracking-[0.2em] mt-1">Investment Compliance Protocol</p>
              </div>
              <button 
                onClick={closeModal} 
                className="h-12 w-12 flex items-center justify-center rounded-2xl bg-slate-900 text-slate-400 hover:text-white hover:bg-slate-800 transition-all border border-slate-800"
              >
                <X size={24} />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-10 space-y-8 overflow-y-auto max-h-[75vh]">
              {/* Searchable Investor Dropdown */}
              <div className="space-y-3 relative" ref={userDropdownRef}>
                <label className="text-[10px] uppercase font-black tracking-[0.25em] text-red-500">Assigned Investor Entity</label>
                <div className="relative group">
                  <div className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-500 group-focus-within:text-red-500 transition-colors">
                    <User size={18} />
                  </div>
                  <input
                    type="text"
                    required
                    placeholder="Search by Name or Email..."
                    className="w-full bg-slate-950/50 border border-slate-800 rounded-2xl px-5 py-4 pl-14 text-sm text-slate-100 focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all font-bold placeholder:font-medium"
                    value={userSearch}
                    onChange={(e) => {
                      setUserSearch(e.target.value);
                      setShowUserDropdown(true);
                    }}
                    onFocus={() => setShowUserDropdown(true)}
                  />
                  <ChevronDown 
                    className={`absolute right-5 top-1/2 -translate-y-1/2 text-slate-600 transition-transform duration-300 ${showUserDropdown ? 'rotate-180' : ''}`} 
                    size={20} 
                  />
                </div>
                
                {showUserDropdown && (
                  <div className="absolute top-full left-0 right-0 mt-2 bg-slate-900 border border-slate-800 rounded-[2rem] shadow-2xl z-[110] overflow-hidden max-h-[250px] overflow-y-auto backdrop-blur-xl animate-in fade-in slide-in-from-top-4 duration-300">
                    <div className="p-2 space-y-1">
                      {filteredUsers.length > 0 ? (
                        filteredUsers.map((u) => (
                          <button
                            key={u.id}
                            type="button"
                            onClick={() => {
                              setFormData({ ...formData, user_id: u.id });
                              setUserSearch(u.name);
                              setShowUserDropdown(false);
                            }}
                            className="w-full flex items-center justify-between p-4 rounded-2xl hover:bg-red-500/10 text-left group transition-all"
                          >
                            <div className="flex items-center gap-4">
                              <div className="h-10 w-10 rounded-xl bg-slate-800 flex items-center justify-center text-slate-500 group-hover:text-red-500 transition-colors font-black">
                                {u.name.charAt(0)}
                              </div>
                              <div>
                                <p className="text-sm font-black text-slate-200 group-hover:text-white">{u.name}</p>
                                <p className="text-[10px] text-slate-600 font-mono tracking-tighter uppercase">{u.email}</p>
                              </div>
                            </div>
                            <div className="text-[10px] font-black bg-slate-950 px-2 py-1 rounded-lg border border-slate-800 text-slate-500">
                              UID-{u.id}
                            </div>
                          </button>
                        ))
                      ) : (
                        <div className="p-8 text-center">
                          <p className="text-sm text-slate-500 italic">No matching investors found.</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-3">
                  <label className="text-[10px] uppercase font-black tracking-[0.25em] text-slate-500">Investment Reference #</label>
                  <div className="relative group">
                    <div className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-600 group-focus-within:text-red-500 transition-colors">
                      <Briefcase size={18} />
                    </div>
                    <input
                      type="text"
                      readOnly
                      className="w-full bg-slate-950/30 border border-slate-800 rounded-2xl px-5 py-4 pl-14 text-sm text-slate-500 focus:outline-none font-mono cursor-not-allowed"
                      value={editingInvestment ? formData.investment_number : 'AUTO-GENERATED'}
                      placeholder="INV-XXXX"
                    />
                  </div>
                </div>

                <div className="space-y-3">
                  <label className="text-[10px] uppercase font-black tracking-[0.25em] text-slate-500">Asset Strategy Class</label>
                  <div className="relative">
                    <select
                      required
                      className="w-full bg-slate-950/50 border border-slate-800 rounded-2xl px-5 py-4 text-sm text-slate-100 focus:outline-none focus:border-red-500 transition-all appearance-none font-bold"
                      value={formData.type}
                      onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    >
                      <option value="share">Share (Equity)</option>
                      <option value="bond">Bond (Debt)</option>
                      <option value="mutual_fund">Mutual Fund</option>
                      <option value="fixed_deposit">Fixed Deposit</option>
                    </select>
                    <ChevronDown className="absolute right-5 top-1/2 -translate-y-1/2 text-slate-600 pointer-events-none" size={20} />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-3">
                  <label className="text-[10px] uppercase font-black tracking-[0.25em] text-slate-500">Capital Deployment (₹)</label>
                  <div className="relative group">
                    <div className="absolute left-5 top-1/2 -translate-y-1/2 text-red-500">
                      <IndianRupee size={20} />
                    </div>
                    <input
                      type="number"
                      step="0.01"
                      required
                      className="w-full bg-slate-950/50 border border-slate-800 rounded-2xl px-5 py-4 pl-14 text-xl font-black text-white focus:outline-none focus:border-red-500 transition-all tracking-tight"
                      value={formData.amount}
                      onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                      placeholder="0.00"
                    />
                  </div>
                </div>

                <div className="space-y-3">
                  <label className="text-[10px] uppercase font-black tracking-[0.25em] text-slate-500">Annual Return Rate (%)</label>
                  <div className="relative group">
                    <div className="absolute left-5 top-1/2 -translate-y-1/2 text-red-500">
                      <TrendingUp size={20} />
                    </div>
                    <input
                      type="number"
                      step="0.01"
                      required
                      className="w-full bg-slate-950/50 border border-slate-800 rounded-2xl px-5 py-4 pl-14 text-xl font-black text-white focus:outline-none focus:border-red-500 transition-all tracking-tight"
                      value={formData.interest_rate}
                      onChange={(e) => setFormData({ ...formData, interest_rate: e.target.value })}
                      placeholder="0.00"
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 pt-4">
                <div className="space-y-3">
                  <label className="text-[10px] uppercase font-black tracking-[0.25em] text-slate-500">Start Date</label>
                  <input
                    type="date"
                    required
                    className="w-full bg-slate-950/50 border border-slate-800 rounded-2xl px-6 py-4 text-sm text-slate-100 focus:outline-none focus:border-red-500 transition-all font-mono"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  />
                </div>
                <div className="space-y-3">
                  <label className="text-[10px] uppercase font-black tracking-[0.25em] text-slate-500">Maturity Target</label>
                  <input
                    type="date"
                    className="w-full bg-slate-950/50 border border-slate-800 rounded-2xl px-6 py-4 text-sm text-slate-100 focus:outline-none focus:border-red-500 transition-all font-mono"
                    value={formData.maturity_date}
                    onChange={(e) => setFormData({ ...formData, maturity_date: e.target.value })}
                  />
                </div>
              </div>

              <div className="space-y-4 pt-4 border-t border-slate-800">
                <label className="text-[10px] uppercase font-black tracking-[0.25em] text-slate-500">Operational Life Cycle</label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {['pending', 'active', 'matured', 'withdrawn'].map(s => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setFormData({ ...formData, status: s })}
                      className={`py-3 rounded-2xl text-[9px] uppercase font-black tracking-widest border transition-all ${
                        formData.status === s 
                          ? getStatusStyle(s) + ' border-current scale-105 shadow-xl shadow-black/20'
                          : 'bg-slate-950/50 border-slate-800 text-slate-500 hover:border-slate-700'
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex justify-end gap-4 pt-10">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-8 py-4 rounded-2xl text-xs font-black uppercase tracking-[0.2em] text-slate-500 hover:text-white transition-all bg-slate-900 border border-slate-800 hover:bg-slate-800"
                >
                  Discard
                </button>
                <button
                  type="submit"
                  className="px-12 py-4 bg-red-600 text-white rounded-2xl font-black uppercase tracking-[0.2em] hover:bg-red-500 transition-all shadow-2xl shadow-red-900/40 active:scale-95"
                >
                  {editingInvestment ? 'Sync Performance' : 'Confirm Asset'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Print View Component */}
      {selectedForPrint && (
        <InvestmentPrint
          investment={selectedForPrint}
          onClose={() => setSelectedForPrint(null)}
        />
      )}
    </div>
  );
}

export default InvestmentsManagement;

